import {
  POOL_BALL_RADIUS,
  POOL_CUE_BALL,
  POOL_EIGHT_BALL,
  POOL_POCKETS,
  POOL_POCKET_RADIUS,
  POOL_TABLE_LENGTH,
  POOL_TABLE_WIDTH,
  groupOf,
  type PoolBall,
  type PoolGroup,
  type PoolPoint,
} from '@awwyeah/shared';

/**
 * The aiming and ball-in-hand maths behind the pool controls — pure, canvas-free, and
 * unit-tested (`apps/web/test/games/pool.aim.test.ts`). None of this touches physics or the
 * move log: it only decides which `{angle, power, cueBallPlacement}` the player is about to
 * send, so changing anything here never changes how an existing game replays.
 *
 * ## The controls, and why they look like this
 *
 * The first version was "drag back from the cue ball, release fires". On a phone that has two
 * real failures: shooting toward the far end with the cue ball near your own rail means
 * pulling your finger *off the bottom of the board* to get any power, and aiming and power
 * are one gesture, so you can't fix one without disturbing the other. The mobile pool games
 * people stick with (Miniclip's 8 Ball Pool above all) split them:
 *
 *  - **Aim**: touch anywhere on the table and the cue points at your finger; drag to swing
 *    it. Nothing fires.
 *  - **Fine-tune**: a spinner wheel under the table turns the aim by a fraction of a degree
 *    per pixel (`nudgeAngle`), for the last bit of precision a fingertip can't give.
 *  - **Power**: a separate pull-down bar beside the table. Pull down, let go to shoot; slide
 *    back to the top and let go to cancel (`powerFromPull`). A tap is zero pull, so an
 *    accidental touch can't take your shot.
 */

/** Below this pull fraction, letting go of the power bar is a cancel, not a shot. */
export const POWER_CANCEL_BELOW = 0.04;

/** Radians of aim per pixel dragged on the fine-tune wheel — ~0.06°, so a full thumb swipe
 * (~200px) is about 12°. */
export const FINE_TUNE_RAD_PER_PX = 0.001;

/** Normalises an angle into `(-π, π]` so stored aims stay tidy. */
export function normalizeAngle(angle: number): number {
  let a = angle % (Math.PI * 2);
  if (a <= -Math.PI) a += Math.PI * 2;
  if (a > Math.PI) a -= Math.PI * 2;
  return a;
}

/**
 * "Point the cue at my finger." Returns `null` when the finger is on top of the cue ball,
 * where a direction would be noise.
 */
export function aimToward(cueBall: PoolPoint, finger: PoolPoint): number | null {
  const dx = finger.x - cueBall.x;
  const dy = finger.y - cueBall.y;
  if (Math.hypot(dx, dy) < POOL_BALL_RADIUS * 1.2) return null;
  return Math.atan2(dy, dx);
}

/** The fine-tune wheel: pixels dragged → a small turn of the aim. */
export function nudgeAngle(angle: number, deltaPx: number): number {
  return normalizeAngle(angle + deltaPx * FINE_TUNE_RAD_PER_PX);
}

/**
 * The power bar. `pulledPx` is how far the finger has moved *down* since it touched the bar
 * (negative = back up past where it started); `fullPx` is the pull that means full power.
 * `cancel` is true when letting go now should do nothing.
 */
export function powerFromPull(
  pulledPx: number,
  fullPx: number,
): { power: number; cancel: boolean } {
  const power = Math.min(1, Math.max(0, fullPx > 0 ? pulledPx / fullPx : 0));
  return { power, cancel: power < POWER_CANCEL_BELOW };
}

/**
 * The default aim at the start of a turn: centre-to-centre at the nearest ball you're allowed
 * to hit first (your group, the 8 once you're on it, anything but the 8 on an open table).
 * Just a starting point so the cue never begins pointing at a wall — you still pick the cut.
 */
export function defaultAim(
  balls: readonly PoolBall[],
  cueBall: PoolPoint,
  target: PoolGroup | 'eight' | null,
): number {
  const legal = (n: number): boolean => {
    if (n === POOL_CUE_BALL) return false;
    if (target === 'eight') return n === POOL_EIGHT_BALL;
    if (target === null) return n !== POOL_EIGHT_BALL;
    return groupOf(n) === target;
  };
  let best: PoolBall | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const ball of balls) {
    if (!legal(ball.n)) continue;
    const d = Math.hypot(ball.x - cueBall.x, ball.y - cueBall.y);
    if (d < bestD) {
      bestD = d;
      best = ball;
    }
  }
  if (!best) return 0;
  return Math.atan2(best.y - cueBall.y, best.x - cueBall.x);
}

// ---- ball in hand ------------------------------------------------------------------------

/** Hair of clearance so a placed cue ball never starts *touching* another ball or a rail —
 * a resting contact on step one is a free solver impulse nobody asked for. */
const CLEARANCE = POOL_BALL_RADIUS * 0.04;

/**
 * Where the client lets you drop the cue ball. Deliberately *stricter* than the rules
 * engine's `isPlacementLegal` (packages/shared), never looser: everything allowed here is
 * also legal there, so every new move derives — and the shared check is left exactly as it
 * was, so no move already in a production log can start being rejected.
 *
 * The extra restriction is the pocket mouths. A cue ball dropped at (r, r) is "on the
 * table" by the rectangle test but its centre is already inside the corner pocket's capture
 * radius, so it vanished on the first physics step — a scratch you never shot, which looked
 * exactly like the ball falling off the board.
 */
export function isPlacementSafe(balls: readonly PoolBall[], at: PoolPoint): boolean {
  const r = POOL_BALL_RADIUS;
  if (!Number.isFinite(at.x) || !Number.isFinite(at.y)) return false;
  if (at.x < r + CLEARANCE || at.x > POOL_TABLE_LENGTH - r - CLEARANCE) return false;
  if (at.y < r + CLEARANCE || at.y > POOL_TABLE_WIDTH - r - CLEARANCE) return false;
  if (POOL_POCKETS.some((p) => Math.hypot(p.x - at.x, p.y - at.y) < POOL_POCKET_RADIUS + r)) {
    return false;
  }
  return !balls.some(
    (b) => b.n !== POOL_CUE_BALL && Math.hypot(b.x - at.x, b.y - at.y) < r * 2 + CLEARANCE,
  );
}

/** Clamps a point onto the felt, inside the cushions. */
export function clampToFelt(at: PoolPoint): PoolPoint {
  const m = POOL_BALL_RADIUS + CLEARANCE;
  return {
    x: Math.min(Math.max(at.x, m), POOL_TABLE_LENGTH - m),
    y: Math.min(Math.max(at.y, m), POOL_TABLE_WIDTH - m),
  };
}

/**
 * Ball in hand: where the cue ball actually goes when your finger is at `desired`. Clamped
 * onto the felt, then — if that spot overlaps a ball or sits in a pocket mouth — moved to the
 * nearest safe spot, searched outward in rings (deterministic, so the ball doesn't jitter
 * between candidates as the finger moves). `legal` is false when the ball had to be moved
 * off the finger's spot — that's what the red ring under your finger shows.
 */
export function placeCueBall(
  balls: readonly PoolBall[],
  desired: PoolPoint,
): { at: PoolPoint; legal: boolean } {
  const clamped = clampToFelt(desired);
  // Off the edge just slides along the cushion — that's still "where you put it".
  if (isPlacementSafe(balls, clamped)) return { at: clamped, legal: true };
  const step = POOL_BALL_RADIUS * 0.25;
  for (let ring = 1; ring <= 40; ring += 1) {
    const radius = ring * step;
    const samples = 12 + ring * 4;
    let best: PoolPoint | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (let i = 0; i < samples; i += 1) {
      const t = (i / samples) * Math.PI * 2;
      const candidate = clampToFelt({
        x: clamped.x + Math.cos(t) * radius,
        y: clamped.y + Math.sin(t) * radius,
      });
      if (!isPlacementSafe(balls, candidate)) continue;
      const d = Math.hypot(candidate.x - clamped.x, candidate.y - clamped.y);
      if (d < bestD) {
        bestD = d;
        best = candidate;
      }
    }
    if (best) return { at: best, legal: false };
  }
  // A table so full there's nowhere near — fall back to the head spot area scan.
  for (let x = 0.3; x < POOL_TABLE_LENGTH; x += 0.1) {
    for (let y = 0.3; y < POOL_TABLE_WIDTH; y += 0.1) {
      if (isPlacementSafe(balls, { x, y })) return { at: { x, y }, legal: false };
    }
  }
  return { at: clamped, legal: false };
}

/** Is a touch close enough to the cue ball to mean "pick it up" rather than "aim"? Measured
 * in CSS pixels so it's a real finger-sized target (≥ 44px across) at any table scale. */
export function touchesCueBall(
  touchPx: { cx: number; cy: number },
  cuePx: { cx: number; cy: number },
  ballRadiusPx: number,
): boolean {
  return Math.hypot(touchPx.cx - cuePx.cx, touchPx.cy - cuePx.cy) <= Math.max(22, ballRadiusPx * 2);
}
